import { useState, useEffect } from "react";
import { Navigate, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Mail, LogIn, Check, Clock, Users, Zap, Gauge, Loader2, AlertTriangle } from "lucide-react";
import { checkTenantExists } from "@/services/authService";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { MarketplaceSubscriptionPayload } from "@/lib/types";

// Define error types outside the component (so it's not recreated on each render)
enum SignupErrorKind {
    AuthFailed = "AUTH_FAILED",
    SubscriptionInvalid = "SUBSCRIPTION_INVALID",
    SubscriptionActivationFailed = "SUBSCRIPTION_ACTIVATION_FAILED",
    MissingToken = "MISSING_TOKEN",
}

interface SignupError {
    kind: SignupErrorKind;
    technicalDetails?: unknown;
}

// Error message mapping (outside component for reusability)
const ERROR_MESSAGES: Record<SignupErrorKind, { title: string; message: string }> = {
    [SignupErrorKind.AuthFailed]: {
        title: "Authentication Failed",
        message: "Failed to connect to Microsoft. Please try to login using your Microsoft 365 / Entra ID account.",
    },
    [SignupErrorKind.SubscriptionInvalid]: {
        title: "Invalid Subscription",
        message: "We couldn't identify this purchase. Reopen this SaaS subscription in the Azure portal or in Microsoft 365 Admin Center and select \"Configure Account\" or \"Manage Account\" again.",
    },
    [SignupErrorKind.SubscriptionActivationFailed]: {
        title: "Activation Failed",
        message: "Failed to activate marketplace subscription. Please contact support.",
    },
    [SignupErrorKind.MissingToken]: {
        title: "Missing Token",
        message: "The Microsoft Marketplace subscription token is missing. Reopen this SaaS subscription in the Azure portal or in Microsoft 365 Admin Center and select \"Configure Account\" or \"Manage Account\" again.",
    },
};

export default function MsSignupPage() {
    const { user, isLoading, signInWithMicrosoft } = useAuth();
    const [marketplaceToken, setMarketplaceToken] = useState<string | null>(null);
    const [marketplaceSubscription, setMarketplaceSubscription] = useState<MarketplaceSubscriptionPayload | null>(null);
    const [isLoadingSubscription, setIsLoadingSubscription] = useState<boolean>(true);
    const [isActivationSuccess, setIsActivationSuccess] = useState<boolean>(false);
    const [isAlreadyActive, setIsAlreadyActive] = useState<boolean>(false);
    const [tenantExists, setTenantExists] = useState<boolean>(false);
    const [error, setError] = useState<SignupError | null>(null);
    const [companyError, setCompanyError] = useState<string | null>(null);
    const [subscriptionConflict, setSubscriptionConflict] = useState<'has-free' | 'has-standard' | null>(null);

    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const token = params.get('token');

        if (token) {
            setMarketplaceToken(token);
        }
        else {
            console.error("MsSignupPage - No token found in URL");
            setError({
                kind: SignupErrorKind.MissingToken,
            });
        }
    }, [location.search]);

    // if not logged in, trigger Microsoft login
    useEffect(() => {
        if (marketplaceToken && !user && !isLoading) {
            console.log("SignupPage - Marketplace token detected, initiating OAuth with state");

            // Trigger Microsoft login with the token in state
            signInWithMicrosoft(window.location.origin + "/ms-signup", { token: marketplaceToken }).catch(error => {
                console.error("Failed to initiate Microsoft login:", error);
                setError({
                    kind: SignupErrorKind.AuthFailed,
                    technicalDetails: error,
                });
            });
        }
    }, [marketplaceToken, user, isLoading, signInWithMicrosoft]);


    // Process marketplace subscription after authentication
    useEffect(() => {
        // Use the stored marketplace state
        if (!user || !marketplaceToken) return;

        console.log("SignupPage - Processing marketplace subscription with token");

        supabase.functions.invoke('ms-marketplace-resolve-subscription', {
            body: { token: marketplaceToken }
        })
            .then(({ data, error }) => {
                if (error) {
                    console.error("SignupPage - Marketplace subscription error:", error);
                    setError({
                        kind: SignupErrorKind.SubscriptionInvalid,
                        technicalDetails: error,
                    });
                    toast.error(ERROR_MESSAGES[SignupErrorKind.SubscriptionInvalid].title);
                    return;
                } else {
                    setMarketplaceSubscription(data as MarketplaceSubscriptionPayload);

                    // Check if subscription is pending activation
                    const status = (data as MarketplaceSubscriptionPayload)?.subscription?.saasSubscriptionStatus;
                    console.log("SignupPage - Subscription status:", status);

                    if (status !== "PendingFulfillmentStart") {
                        console.log("SignupPage - Subscription already active or not in pending state");
                        setIsAlreadyActive(true);
                    }

                    // Check for subscription plan conflicts
                    const existingSubscriptions = (data as any).existingSubscriptions as { id: string; plan_id: string; status: string }[] | undefined;
                    const newPlanId = (data as MarketplaceSubscriptionPayload)?.subscription?.planId;
                    if (existingSubscriptions && existingSubscriptions.length > 0) {
                        const hasStandard = existingSubscriptions.some(s => s.plan_id === 'ar365-standard');
                        const hasFree = existingSubscriptions.some(s => s.plan_id === 'ar365-free');
                        if (hasStandard) {
                            setSubscriptionConflict('has-standard');
                        } else if (hasFree && newPlanId === 'ar365-free') {
                            setSubscriptionConflict('has-free');
                        }
                    }
                }
            })
            .finally(() => {
                setIsLoadingSubscription(false);
            });
    }, [user, marketplaceToken]);

    useEffect(() => {
        const tenantId = marketplaceSubscription?.subscription?.beneficiary?.tenantId ?? null;
        if (tenantId) {
            checkTenantExists(tenantId).then(tenantExists => {
                console.log("MsSignupPage - Tenant exists:", tenantExists);
                setTenantExists(tenantExists);
            });
        }
    }, [marketplaceSubscription]);


    const handleMicrosoftSignUp = async () => {
        try {
            await signInWithMicrosoft(window.location.origin + "/ms-signup");
        } catch (error: any) {
            console.error("Signup error:", error);
        }
    };

    const confirmSubscription = async () => {
        const companyInput = (document.getElementById('company') as HTMLInputElement)?.value?.trim();

        if (!marketplaceSubscription) {
            toast.error("Marketplace subscription data is not available.");
            return;
        }

        // Validate company field
        if (!tenantExists && !companyInput) {
            setCompanyError("Company name is required");
            return;
        }

        // Clear error if validation passes
        setCompanyError(null);

        console.log("confirmSubscription invoked with company:", companyInput);

        supabase.functions.invoke('ms-marketplace-activate-subscription', {
            body: {
                token: marketplaceToken,
                company_name: companyInput
            }
        }).then(({ data, error }) => {
                if (error) {
                    console.error("SignupPage - Marketplace activate subscription error:", error);
                    setError({
                        kind: SignupErrorKind.SubscriptionActivationFailed,
                        technicalDetails: error,
                    });
                    toast.error(ERROR_MESSAGES[SignupErrorKind.SubscriptionActivationFailed].title);
                    return;
                } else {
                    toast.success("Subscription activated successfully!");
                    setIsActivationSuccess(true);
                }
            })


    }

    // Show error state if marketplace processing failed
    if (error) {
        return (
            <div className="flex min-h-screen bg-background items-center justify-center">
                <div className="text-center max-w-md">
                    <div className="text-2xl font-bold mb-4">AutoReply365 Subscription Activation</div>
                    <div className="text-3xl mb-4">⚠️</div>
                    <h2 className="text-xl font-bold text-red-600 mb-2">{ERROR_MESSAGES[error.kind].title}</h2>
                    <p className="text-muted-foreground mb-4">{ERROR_MESSAGES[error.kind].message}</p>

                    {error.kind === SignupErrorKind.AuthFailed &&
                        <Button
                            type="button"
                            size="lg"
                            className="w-full h-12 text-base font-medium mb-5"
                            onClick={handleMicrosoftSignUp}
                            disabled={isLoading}
                        >
                            <LogIn className="mr-3 h-5 w-5" />
                            Login with Microsoft 365
                        </Button>
                    }

                    <p>
                        If this issue persists, please <a className="text-blue-600 underline" href="https://www.autoreply365.com/contact" target="_blank">contact our support team</a> for assistance.
                    </p>
                </div>
            </div>
        );
    }

    // If user is already logged in and registered, redirect to dashboard
    // if (user && authStatus.isRegistered && !authStatus.isChecking) {
    //     return <Navigate to="/" />;
    // }

    return (
        <div className="flex min-h-screen bg-background">
            {/* Left side - Hero content */}
            <div className="hidden lg:flex lg:w-1/3 bg-muted/20 relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-primary/10" />
                <div className="relative z-10 flex flex-col justify-center px-12 xl:px-16">
                    <div className="max-w-lg">
                        <Badge variant="secondary" className="mb-6 w-fit">
                            <Clock className="w-3 h-3 mr-1" />
                            14-day free trial
                        </Badge>

                        <h1 className="text-4xl xl:text-5xl font-bold text-foreground mb-6 leading-tight">
                            Automate your Microsoft 365
                            <span className="text-primary"> shared mailboxes</span>
                        </h1>

                        <p className="text-lg text-muted-foreground mb-8 leading-relaxed">
                            Transform your team's email management with intelligent auto-replies.
                            Perfect for support teams, sales inquiries, and shared inboxes.
                        </p>

                        <div className="space-y-4 mb-8">
                            <div className="flex items-start space-x-3">
                                <div className="flex-shrink-0 w-5 h-5 rounded-full bg-primary/10 flex items-center justify-center mt-0.5">
                                    <Check className="w-3 h-3 text-primary" />
                                </div>
                                <div>
                                    <p className="font-medium text-foreground">Smart Auto-Replies</p>
                                    <p className="text-sm text-muted-foreground">Intelligent responses based on email content and sender</p>
                                </div>
                            </div>

                            <div className="flex items-start space-x-3">
                                <div className="flex-shrink-0 w-5 h-5 rounded-full bg-primary/10 flex items-center justify-center mt-0.5">
                                    <Users className="w-3 h-3 text-primary" />
                                </div>
                                <div>
                                    <p className="font-medium text-foreground">Shared Mailbox Support</p>
                                    <p className="text-sm text-muted-foreground">Works seamlessly with Microsoft 365 shared mailboxes</p>
                                </div>
                            </div>

                            <div className="flex items-start space-x-3">
                                <div className="flex-shrink-0 w-5 h-5 rounded-full bg-primary/10 flex items-center justify-center mt-0.5">
                                    <Zap className="w-3 h-3 text-primary" />
                                </div>
                                <div>
                                    <p className="font-medium text-foreground">Instant Setup</p>
                                    <p className="text-sm text-muted-foreground">Connect your Microsoft account and start automating in minutes</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

            </div>

            {/* Right side - Sign up form */}
            <div className="flex-1 flex lg:w-2/3 items-center justify-center p-8 lg:p-12">
                <div className="w-full max-w-lg">

                    {(isLoadingSubscription && !error) ? (
                        <div className="flex items-center justify-center py-8">
                            <Loader2 className="w-8 h-8 text-primary animate-spin" />
                            <span className="ml-3 text-sm text-muted-foreground">Loading subscription details...</span>
                        </div>
                    ) : isActivationSuccess ? (
                        <div className="text-center">
                            <div className="mb-6">
                                <div className="w-20 h-20 rounded-full bg-green-100 dark:bg-green-900/20 flex items-center justify-center mx-auto mb-4">
                                    <Check className="w-10 h-10 text-green-600 dark:text-green-400" />
                                </div>
                                <h2 className="text-3xl font-bold text-foreground mb-2">
                                    Account Activated Successfully!
                                </h2>
                                <p className="text-muted-foreground mb-6">
                                    Your AutoReply365 subscription is now active and ready to use.
                                </p>
                            </div>

                            <div className="rounded-lg bg-primary/5 border border-primary/10 p-6 mb-6 text-left">
                                <h3 className="font-medium text-foreground mb-3">What's Next?</h3>
                                <ul className="space-y-2 text-sm text-muted-foreground">
                                    <li className="flex items-start">
                                        <Check className="w-4 h-4 text-primary mr-2 mt-0.5 flex-shrink-0" />
                                        <span>Connect your Microsoft 365 shared mailboxes</span>
                                    </li>
                                    <li className="flex items-start">
                                        <Check className="w-4 h-4 text-primary mr-2 mt-0.5 flex-shrink-0" />
                                        <span>Set up intelligent auto-reply rules</span>
                                    </li>
                                    <li className="flex items-start">
                                        <Check className="w-4 h-4 text-primary mr-2 mt-0.5 flex-shrink-0" />
                                        <span>Create custom email templates</span>
                                    </li>
                                    <li className="flex items-start">
                                        <Check className="w-4 h-4 text-primary mr-2 mt-0.5 flex-shrink-0" />
                                        <span>Start automating your email responses</span>
                                    </li>
                                </ul>
                            </div>

                            <Button
                                type="button"
                                size="lg"
                                className="w-full h-12 text-base font-medium"
                                onClick={() => navigate("/")}
                            >
                                Take me to the dashboard
                            </Button>

                            <p className="text-sm text-muted-foreground mt-4">
                                Need help getting started? <a href="https://www.autoreply365.com/contact" target="_blank" className="text-primary hover:underline">Contact support</a>.
                            </p>
                        </div>
                    ) : isAlreadyActive ? (
                        <div className="text-center">
                            <div className="mb-6">
                                <div className="w-20 h-20 rounded-full bg-blue-100 dark:bg-blue-900/20 flex items-center justify-center mx-auto mb-4">
                                    <Check className="w-10 h-10 text-blue-600 dark:text-blue-400" />
                                </div>
                                <h2 className="text-3xl font-bold text-foreground mb-2">
                                    Subscription Active
                                </h2>
                                <p className="text-muted-foreground mb-6">
                                    This subscription is active and ready to use.
                                </p>
                            </div>

                            <div className="rounded-lg bg-primary/5 border border-primary/10 p-6 mb-6 text-left">
                                <h3 className="font-medium text-foreground mb-3">Subscription Details</h3>
                                <div className="space-y-2 text-sm">
                                    <div className="flex justify-between">
                                        <span className="text-muted-foreground">Status:</span>
                                        <span className="text-foreground font-medium">{marketplaceSubscription?.subscription?.saasSubscriptionStatus}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-muted-foreground">Tenant ID:</span>
                                        <span className="text-foreground font-mono text-xs">{marketplaceSubscription?.subscription?.beneficiary?.tenantId}</span>
                                    </div>
                                </div>
                            </div>

                            <Button
                                type="button"
                                size="lg"
                                className="w-full h-12 text-base font-medium"
                                onClick={() => navigate("/")}
                            >
                                Go to dashboard
                            </Button>

                            <p className="text-sm text-muted-foreground mt-4">
                                If you need assistance, <a href="https://www.autoreply365.com/contact" target="_blank" className="text-primary hover:underline">contact our support team</a>.
                            </p>
                        </div>
                    ) : (
                        <>
                            <div className="text-center mb-8">
                                <h2 className="text-3xl font-bold text-foreground mb-2">
                                    Get started with AutoReply365
                                </h2>
                                <p className="text-muted-foreground mb-3">
                                    You've been redirected from the Microsoft 365 / Azure Marketplace to verify your subscription details and complete the purchase.
                                </p>
                            </div>


                            <div className="space-y-6">
                                <div className="rounded-lg bg-primary/5 border border-primary/10 p-6">
                                    <div className="flex items-start space-x-3">
                                        <div className="flex-shrink-0">
                                            <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                                                <Gauge className="w-4 h-4 text-primary" />
                                            </div>
                                        </div>
                                        <div className="flex-1">
                                            <h3 className="font-medium text-foreground mb-5">Subscription Details</h3>

                                            <div className="grid grid-cols-1 sm:grid-cols-[auto_1fr] gap-4">
                                                <div className="flex">
                                                    <div className="text-sm text-muted-foreground font-medium pr-2">Service:</div>
                                                </div>
                                                <div className="flex w-full">
                                                    <div className="text-sm text-foreground break-words">AutoReply365</div>
                                                </div>

                                                <div className="flex">
                                                    <div className="text-sm text-muted-foreground font-medium pr-2">Tenant-Id:</div>
                                                </div>
                                                <div className="flex w-full">
                                                    <div className="text-sm text-foreground">{marketplaceSubscription?.subscription?.beneficiary?.tenantId ?? "—"}</div>
                                                </div>

                                                <div className="flex">
                                                    <div className="text-sm text-muted-foreground font-medium pr-2">Purchaser:</div>
                                                </div>
                                                <div className="flex w-full">
                                                    <div className="text-sm text-foreground break-words">{marketplaceSubscription?.subscription?.beneficiary?.emailId ?? "—"}</div>
                                                </div>

                                                <div className="flex">
                                                    <div className="text-sm text-muted-foreground font-medium pr-2">License count:</div>
                                                </div>
                                                <div className="flex w-full">
                                                    <div className="text-sm text-foreground">{marketplaceSubscription?.subscription.quantity ?? "—"}</div>
                                                </div>

                                                <div className="flex">
                                                    <div className="text-sm text-muted-foreground font-medium pr-2">Contract duration:</div>
                                                </div>
                                                <div className="flex w-full">
                                                    <div className="text-sm text-foreground">
                                                        {marketplaceSubscription?.subscription?.term?.termUnit
                                                            ? (marketplaceSubscription?.subscription?.term?.termUnit === "P1Y" ? "yearly" : "monthly")
                                                            : "—"}
                                                    </div>
                                                </div>
                                            </div>

                                            {(!tenantExists) && (
                                                <>
                                                    <h3 className="font-medium text-foreground mt-6 mb-2">Additional Information</h3>
                                                    <div className="text-sm text-muted-foreground  mb-3">
                                                        Please provide your company name:
                                                    </div>

                                                    <div className="flex w-full">
                                                        <input
                                                            id="company"
                                                            name="company"
                                                            type="text"
                                                            defaultValue=""
                                                            placeholder="Enter company name here"
                                                            className={`w-full rounded-md border-2 px-3 py-2.5 text-sm text-foreground placeholder:text-muted-foreground transition-colors ${companyError
                                                                ? 'border-red-500 bg-red-50 dark:bg-red-950/20'
                                                                : 'border-primary/40 bg-primary/8 hover:bg-primary/12 focus:bg-primary/8'
                                                                } focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/30`}
                                                            aria-label="Company name"
                                                            required
                                                        />

                                                    </div>
                                                </>
                                            )}
                                            {companyError && (
                                                <div className="text-sm text-red-600 mt-2 flex items-center">
                                                    <span className="inline-block mr-2">✕</span>
                                                    {companyError}
                                                </div>
                                            )}

                                        </div>
                                    </div>
                                </div>

                                {subscriptionConflict === 'has-free' && (
                                    <Alert variant="destructive">
                                        <AlertTriangle className="h-4 w-4" />
                                        <AlertTitle>Free subscription already active</AlertTitle>
                                        <AlertDescription>
                                            Your organization already has an active Free subscription. You cannot add another Free subscription. Please upgrade to the <strong>Standard plan</strong> instead.
                                        </AlertDescription>
                                    </Alert>
                                )}

                                {subscriptionConflict === 'has-standard' && (
                                    <Alert variant="destructive">
                                        <AlertTriangle className="h-4 w-4" />
                                        <AlertTitle>Standard subscription already active</AlertTitle>
                                        <AlertDescription>
                                            Your organization already has an active Standard subscription. To add more licensed users, please adjust the quantity of your existing subscription in the <strong>Microsoft Admin Center</strong>.
                                        </AlertDescription>
                                    </Alert>
                                )}

                                {!subscriptionConflict && (
                                    <>
                                        <div className="text-center">
                                            <p className="text-sm">
                                                After confirmation, your account will be created automatically.<br /> You'll be able to login right away.
                                            </p>
                                        </div>

                                        <Button
                                            type="button"
                                            size="lg"
                                            className="w-full h-12 text-base font-medium"
                                            onClick={confirmSubscription}
                                        >
                                            <LogIn className="mr-3 h-5 w-5" />
                                            Confirm &amp; complete purchase
                                        </Button>
                                    </>
                                )}

                            </div>
                        </>
                    )}

                </div>
            </div>
        </div>
    );
}
